import { useEffect, useRef, useState } from "react";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { Sparkles, RefreshCw } from "lucide-react";
import { theme, card, emptyFrame, btnPrimary, btnSecondary, inputStyle, type as text } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api, ApiError } from "../api/client";
import { ControlInsightsPanel } from "../components/ControlInsightsPanel";
import type { ControlInsights } from "../components/ControlInsightsPanel";
import { useControlTagData, useTagFilter, matchesTagFilter } from "../components/ControlTags";
import { TierLockModal, TierErrorPayload, TIER_NAMES } from "../components/TierLockModal";
import { JerseyDpSection } from "./OrgProfile";
import { DpThresholdProfileCard, ApplicabilityBadge, DeterminationCallout, RegulationFacts } from "../components/DpThresholdProfile";
import type { Determination, ThresholdsPayload } from "../components/DpThresholdProfile";
import { PortalLink } from "../components/PortalLink";
import { fmtDate } from "../lib/format";
import { lawColor } from "../lib/lawColors";
import { DetailSection } from "../components/RecordView";
import { displayControlRef, displayControlTitle } from "../lib/controlRef";
import { StandardHeader, StandardNote } from "../components/standards/StandardHeader";
import { StatusSummary, countStatuses } from "../components/standards/StatusSummary";
import { ControlFilterBar, statusFilterOptions, matchesControlSearch, isGroupOpen, toggleGroup, type GroupExpansion } from "../components/standards/ControlFilterBar";
import { ControlGroup, ControlGroupStack } from "../components/standards/ControlGroup";
import { ControlRow } from "../components/standards/ControlRow";
import { ControlDetail, evidenceCountFor, type EvidenceDoc, type StatusOption } from "../components/standards/ControlDetail";
import { useStandardPermissions } from "../components/standards/useStandardPermissions";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useRecordChecks } from "../components/RecordChecks";
import { COUNTRY_REGULATIONS } from "../data/countriesData";
import { US_LAW_CATEGORY, US_STATE_OF_LAW } from "../data/usStates";
import { CsrBillReadiness } from "../components/CsrBillReadiness";
import { LawRefreshNotices } from "../components/LawRefreshNotices";

// Legal obligations: every law in the organisation's Operating Locations, one group per law, one
// row per obligation, on the same summary, filter bar, group, row and detail as the standards
// (components/standards/*). What only this page has: the threshold engine's applicability
// verdict with its override, the Jersey DPJL tier guidance, and the on-demand AI analysis.

// ── Types ─────────────────────────────────────────────────────────────────

interface LegalControl {
  id: string;
  frameworkId: string;
  kind: string;
  domain: string;
  name: string;
  description: string;
  requiredEvidence: string[];
  sortOrder: number;
}

interface LegalEntry {
  id: string | null;
  applicable: boolean | null;
  justification: string | null;
  aiNote: string | null;
  status: "COMPLIANT" | "GAP" | "IN_PROGRESS" | "NOT_STARTED";
  approvalStatus: string;
  updatedAt: string | null;
}

interface SoaRow {
  control: LegalControl;
  entry: LegalEntry;
  /** Threshold determination for this obligation, when the engine has a rule for it. */
  applicability: Determination | null;
  /** Effective applicability: explicit override on the entry, else the engine's verdict. */
  applicable: boolean;
  /** Marked not applicable although the engine says it is required: the incident duty stays (backend lib/legalObligations.ts). */
  overrideConflict?: boolean;
}

interface LegalSoaResponse {
  soa: SoaRow[];
  regulations: string[];
  thresholds: ThresholdsPayload | null;
  regulationFacts: Record<string, Determination[]>;
  regulationFamilies?: Record<string, string | null>;
  /** Laws a Data Protection Officer (privacy:write) may track obligations of (routes/legal.routes.ts). */
  dataProtectionRegulations?: string[];
}

// ── JDPL tier-specific guidance per obligation ────────────────────────────

const JDPL_TIER_NOTES: Record<string, Partial<Record<string, string>>> = {
  "PRINCIPLES": {
    sole_trader: "As a sole trader, you personally are the data controller. A simple one-page privacy notice and a note of your lawful basis for each data type will demonstrate compliance with all six principles.",
    small: "All six principles apply. Prioritise a clear privacy notice, documented lawful basis for each processing type, and basic staff awareness. Appoint a named Data Protection Contact.",
    medium: "Document the lawful basis for every processing activity and review privacy notices annually. Staff data-protection training should be part of your induction process.",
    large: "Ensure cross-functional governance structures enforce the six principles across all departments. Privacy-by-design reviews are expected for all new projects.",
    charity: "All six principles apply to charities. Pay particular attention to donor consent, beneficiary data minimisation, and transparency — individuals should always know how their data is used.",
  },
  "RIGHTS": {
    sole_trader: "All JDPL rights apply. With a small number of data subjects, handle requests individually and document each one. You must respond within one calendar month.",
    small: "Designate someone to handle data subject rights requests and ensure they know the one-month deadline. Log each request for accountability even if the volume is low.",
    medium: "Document your rights-request process. Right-to-erasure and rectification requests are increasingly likely at this size. Log all requests and track response times.",
    large: "A formal rights-request process with a dedicated inbox and tracking system is essential. Volume may require semi-automated workflows to meet the one-month deadline consistently.",
    charity: "All rights apply to donors, beneficiaries, and volunteers. Be particularly careful where right-to-erasure conflicts with a legal obligation to retain records (e.g. grant audit trails).",
  },
  "BREACH": {
    sole_trader: "You must notify JOIC within 72 hours of becoming aware of any breach that poses a risk to individuals. Keep a simple breach log and have a basic response plan written down.",
    small: "Designate someone responsible for breach identification and reporting. Have a written incident response process, even a brief one, so the 72-hour window is not missed.",
    medium: "Maintain a breach register and a tested incident response plan. With more systems and staff, the likelihood of discovering a breach increases.",
    large: "A formal incident response policy with defined escalation paths is expected at this scale. Breach scenarios should be tested at least annually and your breach register reviewed regularly.",
    charity: "Breaches involving donor or beneficiary data can be especially sensitive. Report to JOIC within 72 hours if individuals are at risk, and consider notifying affected individuals directly.",
  },
  "SPECIAL CAT": {
    sole_trader: "If you process health, biometric, or other special category data, you must identify an explicit Schedule 2 condition. For most sole traders, explicit consent is the most appropriate basis.",
    small: "Document both an Article 6 lawful basis and a Schedule 2 condition for every special category processing activity. For employee health data, the employment condition typically applies.",
    medium: "Systematically review all processing that may touch special category data. Each activity needs both bases documented. DPIAs are frequently triggered by special category processing.",
    large: "A data inventory mapping every special category processing activity to its Schedule 2 condition is required. Large-scale processing of sensitive data is a key DPIA trigger.",
    charity: "Charities often hold sensitive beneficiary data. Ensure every special category activity has a documented Schedule 2 condition, and review whether explicit consent is the most appropriate basis.",
  },
  "DPO": {
    sole_trader: "A DPO is not mandatory for sole traders. However, JOIC strongly recommends appointing a named Data Protection Contact to handle enquiries and breaches.",
    small: "A DPO is not mandatory. Appoint a named Data Protection Contact — this can be you or a member of staff — and make their details available to staff and data subjects.",
    medium: "A DPO is not automatically required, but review whether your processing involves large-scale systematic monitoring or large-scale special category data, which would trigger the obligation.",
    large: "Review all three DPO trigger criteria: public authority functions, large-scale systematic monitoring, and large-scale special category data processing. If any apply, a formally designated, qualified DPO is mandatory.",
    charity: "A DPO is not automatically required for charities, but large-scale processing of sensitive beneficiary data may trigger it. A named Data Protection Contact is recommended for all charities.",
  },
  "PROCESSOR AG": {
    sole_trader: "Any third party storing or processing personal data on your behalf — cloud storage, payroll, website hosting — must have a written Article 28-compliant contract in place before processing begins.",
    small: "Review all software subscriptions and service providers. Those accessing or storing personal data on your behalf are processors and must have a written DPA signed before use.",
    medium: "Maintain a processor register. Review each processor's security measures and ensure contracts include the required Article 28 clauses. Revisit DPAs when renewing supplier contracts.",
    large: "A formal vendor management process with mandatory DPA sign-off for all new suppliers is expected. Your processor register should be kept current and reviewed at least annually.",
    charity: "Charities commonly use donor CRMs, grant management systems, and volunteer platforms — all likely processors. Ensure written DPAs cover data location and sub-processor obligations.",
  },
  "ROPA": {
    sole_trader: "Exempt from ROPA if your processing is occasional, doesn't involve special category data, and poses little risk. If you process customer or employee data regularly, a simple spreadsheet ROPA is advisable.",
    small: "Exempt from ROPA if processing is occasional and doesn't involve special category or criminal offence data. If you regularly process employee or customer personal data, a ROPA is recommended.",
    medium: "Review each processing activity individually. Regular processing or any involving special category data requires a ROPA entry. A medium organisation is very likely to need a partial or full ROPA.",
    large: "With 50+ FTEs, you are almost certainly required to maintain a comprehensive ROPA covering all processing activities across all departments. Review it at least annually.",
    charity: "Review each processing activity — donor campaigns, grant processing, beneficiary records, volunteer management. Regular processing or sensitive data requires ROPA entries.",
  },
  "DPIA": {
    sole_trader: "DPIAs are required for high-risk processing regardless of size. New technology that monitors individuals, processes location data, or handles sensitive data at scale needs a DPIA first.",
    small: "DPIAs are most likely triggered for small organisations by new HR systems, CCTV installation, or health data processing. Screen new systems for high-risk indicators before deployment.",
    medium: "Embed a DPIA screening step into your project and procurement process. The volume and variety of data you process increases the frequency of DPIA triggers.",
    large: "A mandatory DPIA screening gate should be built into project governance. Completed DPIAs must be reviewed whenever the underlying processing changes materially.",
    charity: "DPIAs are required when processing vulnerable beneficiary data using profiling or automated systems. Conduct a DPIA before deploying any new technology involving sensitive personal data.",
  },
  "TRANSFERS": {
    sole_trader: "If you use cloud services or software hosted outside Jersey or the UK, you may be making international data transfers. Check whether the destination has an adequacy decision or another valid transfer mechanism.",
    small: "Review where your SaaS tools store or process data. Many common services (email, HR, CRM) transfer data outside Jersey. Ensure each transfer has a valid legal mechanism documented.",
    medium: "Conduct a transfer mapping exercise to identify all international data flows. Standard contractual clauses or adequacy decisions are the most common mechanisms. Document each in your ROPA.",
    large: "Maintain a transfer impact assessment (TIA) process for transfers to high-risk destinations. Keep transfer records in your ROPA and review them when supplier data locations change.",
    charity: "International partnerships or operations may involve cross-border data flows, especially for beneficiary data. Ensure appropriate safeguards are in place and documented for all international transfers.",
  },
};

// ── Constants ─────────────────────────────────────────────────────────────

const CYBERSECURITY_STANDARDS = new Set([
  "NIS2 Directive",
  "EU Cybersecurity Act 2019 (CSA)",
  "EU Cyber Resilience Act 2024 (CRA)",
]);

const REGULATION_TO_COUNTRY: Record<string, string> = {
  "EU GDPR": "EU / EEA",
  "NIS2 Directive": "EU / EEA",
  "EU Cybersecurity Act 2019 (CSA)": "EU / EEA",
  "EU Cyber Resilience Act 2024 (CRA)": "EU / EEA",
  "UK GDPR": "United Kingdom",
  "Data Protection Act 2018": "United Kingdom",
  "NIS Regulations 2018": "United Kingdom",
  "Jersey Data Protection Law 2018": "Jersey",
  "Computer Misuse (Jersey) Law 1995": "Jersey",
  "Cyber Security (Jersey) Law 2026": "Jersey",
  "Cybercrime (Jersey) Law 2019": "Jersey",
  "PIPEDA": "Canada",
  "LGPD (Lei Geral de Proteção de Dados)": "Brazil",
  "Digital Personal Data Protection Act 2023 (DPDP)": "India",
  "Protection of Personal Information Act (POPIA)": "South Africa",
};

const REGULATION_TO_CATEGORY: Record<string, string> = {
  "EU GDPR": "Data Protection",
  "UK GDPR": "Data Protection",
  "Data Protection Act 2018": "Data Protection",
  "Jersey Data Protection Law 2018": "Data Protection",
  "LGPD (Lei Geral de Proteção de Dados)": "Data Protection",
  "CCPA / CPRA (California)": "Data Protection",
  "PIPEDA": "Data Protection",
  "Digital Personal Data Protection Act 2023 (DPDP)": "Data Protection",
  "Protection of Personal Information Act (POPIA)": "Data Protection",
  "NIS2 Directive": "Cybersecurity",
  "NIS Regulations 2018": "Cybersecurity",
  "EU Cybersecurity Act 2019 (CSA)": "Cybersecurity",
  "EU Cyber Resilience Act 2024 (CRA)": "Cybersecurity",
  "Cyber Security (Jersey) Law 2026": "Cybersecurity",
  "Computer Misuse (Jersey) Law 1995": "Cybercrime",
  "Cybercrime (Jersey) Law 2019": "Cybercrime",
};

/** A US law is filed under its state ("United States — Texas") or "United States — Federal", so the
 *  country filter splits the United States by state; a law that several countries list is an EU / EEA
 *  instrument (the EU AI Act, DORA, the DSA), never filed under whichever member state sorts first;
 *  anything else under its country. */
function regCountry(reg: string): string {
  const state = US_STATE_OF_LAW[reg];
  if (state) return `United States — ${state}`;
  if ((COUNTRY_REGULATIONS["United States"] ?? []).includes(reg)) return "United States — Federal";
  if (REGULATION_TO_COUNTRY[reg]) return REGULATION_TO_COUNTRY[reg];
  const countries = Object.entries(COUNTRY_REGULATIONS).filter(([, laws]) => laws.includes(reg)).map(([c]) => c);
  if (countries.length > 1) return "EU / EEA";
  return countries[0] ?? "Other";
}

/** The category filter's label for each law family the API reports (backend lib/legalObligationTopics.ts). */
const FAMILY_CATEGORY: Record<string, string> = {
  data_protection: "Data Protection",
  cyber_security: "Cybersecurity",
  financial_resilience: "Financial resilience",
  financial_privacy: "Financial privacy",
  offences: "Cybercrime",
  ai: "Artificial intelligence",
  product_security: "Product security",
  online_services: "Online services",
  corporate_records: "Corporate records",
};

function regCategory(reg: string, families: Record<string, string | null> = {}): string {
  const family = families[reg];
  return REGULATION_TO_CATEGORY[reg] ?? (family ? FAMILY_CATEGORY[family] : undefined) ?? US_LAW_CATEGORY[reg] ?? "Other";
}


const LEGAL_STATUS_OPTIONS: StatusOption[] = [
  { value: "NOT_STARTED", label: "Not assessed" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "GAP", label: "Gap identified" },
  { value: "COMPLIANT", label: "Compliant" },
];

const JERSEY_DPJL = "Jersey Data Protection Law 2018";

// ── AI analysis (on demand, per obligation) ──────────────────────────────

type AiState = "idle" | "loading" | "done" | "error";

function LegalAiAnalysis({ control, status, notes, savedInsights, onInsightsSave }: {
  control: LegalControl;
  status: string;
  notes: string;
  savedInsights: ControlInsights | null;
  onInsightsSave: (controlId: string, insights: ControlInsights) => void;
}) {
  // Inside the consultant's client portal, navigation is page state rather than a URL.
  const navigate = usePortalNavigate();
  const [aiState, setAiState] = useState<AiState>(savedInsights ? "done" : "idle");
  const [insights, setInsights] = useState<ControlInsights | null>(savedInsights);
  const [aiError, setAiError] = useState("");
  const [tierError, setTierError] = useState<TierErrorPayload | null>(null);
  const aiAbortRef = useRef<AbortController | null>(null);

  async function loadAiInsights() {
    aiAbortRef.current?.abort();
    const controller = new AbortController();
    aiAbortRef.current = controller;
    setAiState("loading");
    setAiError("");
    try {
      const d = await api.postAbortable<{ evidenceFound: string[]; gaps: string[]; recommendations: string[] }>(
        "/guidance/legal-insights",
        {
          controlId: control.id,
          controlName: control.name,
          regulation: control.domain,
          status,
          notes: notes || undefined,
          requiredEvidence: control.requiredEvidence.length ? control.requiredEvidence : undefined,
        },
        controller.signal,
      );
      const result: ControlInsights = { ...d, status: status as ControlInsights["status"] };
      setInsights(result);
      setAiState("done");
      onInsightsSave(control.id, result);
    } catch (e) {
      if (controller.signal.aborted) { setAiState("idle"); return; }
      if (e instanceof ApiError && e.status === 402) {
        setTierError(e.payload as TierErrorPayload);
        setAiState("error");
      } else {
        setAiError(e instanceof ApiError ? e.message : "Could not generate AI analysis.");
        setAiState("error");
      }
    }
  }

  function cancelAi() {
    aiAbortRef.current?.abort();
    setAiState("idle");
  }

  const small: React.CSSProperties = { ...btnSecondary, padding: "5px 10px", fontSize: text.meta.fontSize, display: "inline-flex", alignItems: "center", gap: 5 };

  return (
    <div>
      {tierError && (
        <TierLockModal
          featureName="AI Compliance Analysis"
          requiredTierName={TIER_NAMES[tierError.requiredLevel] ?? `tier ${tierError.requiredLevel}`}
          requiredTierLevel={tierError.requiredLevel}
          currentTierLevel={tierError.currentLevel}
          onClose={() => setTierError(null)}
          onRequestUpgrade={() => navigate("/my-services")}
        />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {aiState === "idle" && <span style={{ ...text.hint, flex: 1 }}>Generate an AI analysis of how well you meet this obligation.</span>}
        {aiState === "error" && !tierError && <span style={{ ...text.hint, color: theme.color.dangerText, flex: 1 }}>{aiError || "Something went wrong."}</span>}
        {aiState === "loading" ? (
          <>
            <span style={text.meta}>Generating…</span>
            <button type="button" onClick={cancelAi} style={{ ...small, color: theme.color.dangerText, borderColor: theme.color.danger }}>Cancel</button>
          </>
        ) : (
          <button type="button" onClick={loadAiInsights} style={small}>
            {aiState === "done" ? <><RefreshCw size={11} />Refresh</> : <><Sparkles size={11} />Get AI analysis</>}
          </button>
        )}
      </div>
      {aiState === "done" && insights && (
        <ControlInsightsPanel
          insights={insights}
          controlId={control.id}
          controlName={control.name}
          framework={control.domain}
          status={status}
          notes={notes || undefined}
          guidance={control.requiredEvidence}
        />
      )}
    </div>
  );
}

function ClearButton({ onConfirm }: { onConfirm: () => unknown }) {
  const [confirming, setConfirming] = useState(false);
  if (!confirming) {
    return <button type="button" onClick={() => setConfirming(true)} style={{ ...btnSecondary, padding: "6px 12px", fontSize: text.hint.fontSize }}>Clear entry</button>;
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, ...text.hint }}>
      <span style={{ color: theme.color.dangerText }}>Clear this entry?</span>
      <button type="button" onClick={async () => { setConfirming(false); await onConfirm(); }} style={{ ...btnSecondary, padding: "5px 10px", fontSize: text.meta.fontSize, color: theme.color.dangerText, borderColor: theme.color.danger }}>Yes, clear</button>
      <button type="button" onClick={() => setConfirming(false)} style={{ ...btnSecondary, padding: "5px 10px", fontSize: text.meta.fontSize }}>Cancel</button>
    </span>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

const SESSION_KEY = "legal-ai-insights";

function loadInsightsFromSession(): Map<string, ControlInsights> {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return new Map();
    return new Map(JSON.parse(raw) as [string, ControlInsights][]);
  } catch {
    return new Map();
  }
}

export function LegalCompliancePage() {
  // Inside the consultant's client portal, navigation is page state rather than a URL.
  const navigate = usePortalNavigate();
  const { canAssess: canEditControls, canAttach } = useStandardPermissions();
  const { user } = useCurrentUser();
  const recordChecks = useRecordChecks();
  // Tracking an obligation's status: controls:write or legal:write. Deciding it does not apply:
  // legal:write alone (routes/legal.routes.ts).
  const canDecide = !!user?.permissions?.includes("legal:write");
  const canAssess = canEditControls || canDecide;
  const canPrivacy = !!user?.permissions?.includes("privacy:write");
  const tagData = useControlTagData();
  const [tagFilter, setTagFilter] = useTagFilter();
  const [soa, setSoa] = useState<SoaRow[] | null>(null);
  const [docs, setDocs] = useState<EvidenceDoc[]>([]);
  const [dpRegulations, setDpRegulations] = useState<ReadonlySet<string>>(new Set());
  const [regulations, setRegulations] = useState<string[]>([]);
  const [activeCountry, setActiveCountry] = useState("all");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeReg, setActiveReg] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [expansion, setExpansion] = useState<GroupExpansion>("all");
  const [insightsMap, setInsightsMap] = useState<Map<string, ControlInsights>>(loadInsightsFromSession);
  const [thresholds, setThresholds] = useState<ThresholdsPayload | null>(null);
  const [regulationFacts, setRegulationFacts] = useState<Record<string, Determination[]>>({});
  const [regulationFamilies, setRegulationFamilies] = useState<Record<string, string | null>>({});
  // Jersey DPJL tier (stored override or derived from headcount) — drives the JOIC guidance blocks.
  const dpTier = thresholds?.facts.jerseyTier ?? null;

  function handleInsightsSave(controlId: string, insights: ControlInsights) {
    setInsightsMap((prev) => {
      const next = new Map(prev);
      next.set(controlId, insights);
      try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(Array.from(next.entries()))); } catch { /* quota */ }
      return next;
    });
  }

  async function load() {
    const data = await api.get<LegalSoaResponse>("/legal/soa");
    setSoa(data.soa);
    setRegulations(data.regulations);
    setThresholds(data.thresholds ?? null);
    setRegulationFacts(data.regulationFacts ?? {});
    setRegulationFamilies(data.regulationFamilies ?? {});
    setDpRegulations(new Set(data.dataProtectionRegulations ?? []));
  }

  // Evidence is tagged { frameworkId: "legal", controlRef: <obligation id> } — the same shape the register
  // mirrors write. Below the documents tier the list is simply empty rather than breaking the page.
  async function loadDocs() {
    const { documents } = await api.get<{ documents: EvidenceDoc[] }>("/documents").catch(() => ({ documents: [] as EvidenceDoc[] }));
    setDocs(documents);
  }

  useEffect(() => {
    load();
    void loadDocs();
  }, []);

  async function handleUpdate(controlId: string, status: string, notes: string, applicable?: boolean | null): Promise<string | null> {
    setSaving(controlId);
    try {
      await api.put(`/legal/soa/${controlId}`, {
        status,
        notes: notes || undefined,
        ...(applicable !== undefined ? { applicable } : {}),
      });
      await load();
      return null;
    } catch (e) {
      // The server's own words (a missing reason, a permission), never a blank page.
      return e instanceof ApiError || e instanceof Error ? e.message : "Could not save the decision.";
    } finally { setSaving(null); }
  }

  async function handleClear(controlId: string) {
    await api.del(`/legal/soa/${controlId}`);
    await load();
  }

  if (soa === null) {
    return <LoadingBlock />;
  }

  const header = (subtitle: string) => (
    <StandardHeader title="Legal obligations" subtitle={subtitle} />
  );

  if (soa.length === 0) {
    return (
      <div className="page-pad">
        {header("Track your regulatory obligations by jurisdiction, independent of any certification standard.")}
        <div style={{ ...card, maxWidth: 520, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={text.itemTitle}>Set your operating jurisdictions to get started</div>
          <div style={text.hint}>
            The platform maps the data protection, cyber security and industry laws for each country your organisation operates in.
            Add your countries in <strong>My Organisation</strong> and the regulations that apply will appear here.
          </div>
          <div>
            <button type="button" onClick={() => navigate("/org-profile")} style={btnPrimary}>
              Set up My Organisation
            </button>
          </div>
        </div>
        <CsrBillReadiness />
      </div>
    );
  }

  // Obligations the threshold engine exempts (or that were overridden as not applicable) are
  // excluded from the score, as in any statement of applicability.
  const applicableRows = soa.filter((r) => r.applicable);
  const notApplicable = soa.length - applicableRows.length;

  const grouped = new Map<string, SoaRow[]>();
  for (const row of soa) {
    const d = row.control.domain;
    if (!grouped.has(d)) grouped.set(d, []);
    grouped.get(d)!.push(row);
  }

  const allCountries = Array.from(new Set(regulations.map(regCountry))).sort();
  const allCategories = Array.from(new Set(regulations.map((r) => regCategory(r, regulationFamilies)))).sort();

  // Regulations in view, narrowed by country + category; laws first, then the cyber security standards.
  const visibleRegs = regulations
    .filter((reg) => (activeCountry === "all" || regCountry(reg) === activeCountry) && (activeCategory === "all" || regCategory(reg, regulationFamilies) === activeCategory))
    .sort((a, b) => Number(CYBERSECURITY_STANDARDS.has(a)) - Number(CYBERSECURITY_STANDARDS.has(b)));
  const effectiveReg = activeReg !== "all" && visibleRegs.includes(activeReg) ? activeReg : "all";

  const refOf = (r: SoaRow) => displayControlRef(r.control.id);
  const titleOf = (r: SoaRow) => displayControlTitle(r.control.id, r.control.name);
  const rowMatches = (r: SoaRow) =>
    (statusFilter === "all" || r.entry.status === statusFilter)
    && matchesTagFilter(tagData, r.control.id, tagFilter)
    && matchesControlSearch(search, refOf(r), titleOf(r));

  const visibleGroups = (effectiveReg === "all" ? visibleRegs : [effectiveReg])
    .map((reg) => ({ reg, rows: (grouped.get(reg) ?? []).filter(rowMatches) }))
    .filter((g) => g.rows.length > 0);
  const shown = visibleGroups.reduce((n, g) => n + g.rows.length, 0);
  const searching = search.trim().length > 0;

  const regLabel = (reg: string) => {
    const rows = (grouped.get(reg) ?? []).filter((r) => r.applicable);
    const done = rows.filter((r) => r.entry.status === "COMPLIANT").length;
    return `${reg}${CYBERSECURITY_STANDARDS.has(reg) ? " (standard)" : ""} · ${done}/${rows.length}`;
  };
  const selectStyle: React.CSSProperties = { ...inputStyle, width: "auto", maxWidth: 220, padding: "7px 10px" };

  function renderRow(row: SoaRow, reg: string) {
    const { control, entry } = row;
    const isOpen = open === control.id;
    const notes = entry.justification ?? "";
    const displayId = refOf(row);
    const tierNote = reg === JERSEY_DPJL && dpTier ? JDPL_TIER_NOTES[displayId]?.[dpTier] : undefined;
    return (
      <ControlRow
        key={control.id}
        reference={displayId}
        title={titleOf(row)}
        status={entry.status}
        hasNotes={!!entry.justification}
        evidenceCount={evidenceCountFor(docs, "legal", control.id)}
        dimmed={!row.applicable}
        extra={<ApplicabilityBadge determination={row.applicability} applicable={row.applicable} />}
        open={isOpen}
        onToggle={() => setOpen(isOpen ? null : control.id)}
      >
        <ControlDetail
          controlId={control.id}
          requirement={control.description}
          checklist={control.requiredEvidence}
          checklistLabel="Typical evidence"
          recordChecks={recordChecks[control.id]}
          evidence={{ docs, frameworkId: "legal", controlRef: control.id, reload: loadDocs }}
          canAttach={canAttach}
          sections={
            <>
              {/* Always shown: a duty with no threshold test can still be marked not applicable. */}
              <DetailSection title="Applicability">
                <DeterminationCallout
                  determination={row.applicability}
                  override={entry.applicable}
                  applicable={row.applicable}
                  saving={saving === control.id}
                  onOverride={(value, reason) => handleUpdate(control.id, entry.status, reason ?? notes, value)}
                  canDecide={canDecide}
                  reason={entry.justification}
                  reasonMin={thresholds?.notApplicableReasonMin ?? 20}
                  conflict={row.overrideConflict === true}
                />
              </DetailSection>
              {tierNote && (
                <DetailSection title="Guidance for your tier">
                  <div style={text.body}>{tierNote}</div>
                </DetailSection>
              )}
            </>
          }
          status={entry.status}
          aiNote={entry.aiNote}
          assessmentFields={entry.updatedAt ? [{ label: "Last updated", value: fmtDate(entry.updatedAt) }] : undefined}
          statusOptions={LEGAL_STATUS_OPTIONS}
          onSetStatus={(s) => handleUpdate(control.id, s, notes)}
          saving={saving === control.id}
          canEdit={canAssess || (canPrivacy && dpRegulations.has(control.domain))}
          notes={{
            threadId: control.id,
            label: "Notes and justification",
            placeholder: "Add implementation notes or justification…",
            legacyNote: entry.justification,
            onPost: (body) => { handleUpdate(control.id, entry.status, body).catch(() => { /* the note itself is saved */ }); },
          }}
          // A law already names its jurisdiction; the department and office that meet it are the useful tags here.
          tagDimensions={["department", "site"]}
          insights={
            <LegalAiAnalysis
              control={control}
              status={entry.status}
              notes={notes}
              savedInsights={insightsMap.get(control.id) ?? null}
              onInsightsSave={handleInsightsSave}
            />
          }
          actions={(entry.status !== "NOT_STARTED" || notes) ? <ClearButton onConfirm={() => handleClear(control.id)} /> : undefined}
          actionsNote="Clearing resets the status, the summary note and any applicability decision for this obligation. The notes thread below is kept as the record of what was said; delete a note there if it is wrong."
        />
      </ControlRow>
    );
  }

  return (
    <div className="page-pad">
      {header(`${regulations.length} regulation${regulations.length !== 1 ? "s" : ""} apply across your operating jurisdictions: ${applicableRows.length} obligation${applicableRows.length !== 1 ? "s" : ""}${notApplicable > 0 ? `, ${notApplicable} exempt or not applicable` : ""}.`)}

      {/* A refreshed official text of one of these laws, and what of the organisation's rests on it. */}
      <LawRefreshNotices />

      <StatusSummary
        label="Obligations compliant"
        counts={countStatuses(applicableRows.map((r) => r.entry.status))}
        extraCells={notApplicable > 0 ? [{ key: "na", value: notApplicable, label: "Not applicable", color: theme.color.faint }] : undefined}
      />

      {/* Threshold profile — the inputs every law's size and volume tests are run against */}
      <DpThresholdProfileCard thresholds={thresholds} onSaved={load}
        canEdit={!!user?.permissions?.some((p) => p === "legal:write" || p === "privacy:write")} />

      <ControlFilterBar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search obligations"
        status={statusFilter}
        onStatus={setStatusFilter}
        statusOptions={statusFilterOptions(false)}
        group={effectiveReg}
        onGroup={setActiveReg}
        groupOptions={visibleRegs.map((reg) => ({ value: reg, label: regLabel(reg) }))}
        groupLabel="All regulations"
        tagFilter={tagFilter}
        onTagFilter={setTagFilter}
        tagDimensions={["department", "site"]}
        count={{ shown, total: soa.length, noun: "obligations" }}
        onExpandAll={() => setExpansion("all")}
        onCollapseAll={() => setExpansion("none")}
      >
        {allCountries.length > 1 && (
          <select aria-label="Country or jurisdiction" value={activeCountry} onChange={(e) => { setActiveCountry(e.target.value); setActiveReg("all"); }} style={selectStyle}>
            <option value="all">All jurisdictions</option>
            {allCountries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {allCategories.length > 1 && (
          <select aria-label="Category" value={activeCategory} onChange={(e) => { setActiveCategory(e.target.value); setActiveReg("all"); }} style={selectStyle}>
            <option value="all">All categories</option>
            {allCategories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </ControlFilterBar>

      {visibleGroups.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "40px 20px", ...text.hint }}>No obligations match these filters.</div>
      ) : (
        <ControlGroupStack>
          {visibleGroups.map(({ reg, rows }) => {
            const all = grouped.get(reg) ?? [];
            const regApplicable = rows.filter((r) => r.applicable);
            const exempt = all.length - all.filter((r) => r.applicable).length;
            return (
              <ControlGroup
                key={reg}
                title={reg}
                hint={exempt > 0 ? `${exempt} exempt or not applicable` : undefined}
                accent={lawColor(reg, regulations)}
                count={rows.length}
                noun="obligation"
                compliant={regApplicable.filter((r) => r.entry.status === "COMPLIANT").length}
                gap={regApplicable.filter((r) => r.entry.status === "GAP").length}
                extra={CYBERSECURITY_STANDARDS.has(reg) ? (
                  <span style={{ ...text.meta, fontWeight: 700, padding: "1px 7px", borderRadius: 2, background: theme.color.panel, border: `1px solid ${theme.color.borderStrong}` }}>Standard</span>
                ) : undefined}
                expanded={searching || isGroupOpen(expansion, reg)}
                onToggle={() => setExpansion((prev) => toggleGroup(prev, reg, visibleRegs))}
              >
                {(regulationFacts[reg]?.length || reg === JERSEY_DPJL) ? (
                  <div style={{ padding: "12px 14px 0" }}>
                    <RegulationFacts facts={regulationFacts[reg]} />
                    {reg === JERSEY_DPJL && <div style={{ marginBottom: 12 }}><JerseyDpSection dpTier={dpTier} /></div>}
                  </div>
                ) : null}
                {rows.map((row) => renderRow(row, reg))}
              </ControlGroup>
            );
          })}
        </ControlGroupStack>
      )}

      {/* A proposed law, kept apart: never in the summary, the groups or the counts above. */}
      <CsrBillReadiness />

      <StandardNote title="About legal obligation tracking">
        Obligations are derived from the regulations you selected under{" "}
        <PortalLink to="/org-profile" style={{ color: theme.color.accentText, textDecoration: "none" }}>My Organisation, Operating Countries</PortalLink>,
        then tuned to your size, turnover, data volumes and processing using each law's own thresholds. Open an obligation to see the test and its basis, and override it if you know better.
        Mark each obligation as Compliant once you have implemented and documented the required controls, and attach the evidence that shows it under Evidence; use the notes for implementation details and justification.
        Status changes are recorded in the activity log for audit purposes.
      </StandardNote>
    </div>
  );
}
